import { useQuery } from '@tanstack/react-query';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '@/config/firebase';
import type { CollaboratorAllocation, AllocationStatus } from '@/features/Collaborators/Models';
import { AllocationStatuses } from '@/features/Collaborators/Models';
import { collaboratorAllocationsQueryKeys } from './useCollaboratorAllocationsQuery';

export interface UseCollaboratorAllocationQueryResult {
  data: CollaboratorAllocation | null;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

export function useCollaboratorAllocationQuery(
  collaboratorId?: string | null,
  allocationId?: string | null
): UseCollaboratorAllocationQueryResult {
  const parseRate = (value: unknown): number | null => {
    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : null;
    }

    if (typeof value === 'string') {
      const normalized = value.replace(/\./g, '').replace(',', '.');
      const parsed = Number.parseFloat(normalized);
      return Number.isFinite(parsed) ? parsed : null;
    }

    return null;
  };

  const queryResult = useQuery<CollaboratorAllocation | null>({
    enabled: Boolean(collaboratorId && allocationId),
    queryKey:
      collaboratorId && allocationId
        ? collaboratorAllocationsQueryKeys.detail(collaboratorId, allocationId)
        : collaboratorAllocationsQueryKeys.all,
    queryFn: async () => {
      if (!collaboratorId || !allocationId) {
        return null;
      }

      const allocationRef = doc(
        db,
        'collaborators_private',
        collaboratorId,
        'allocations',
        allocationId
      );
      const snapshot = await getDoc(allocationRef);

      if (!snapshot.exists()) {
        return null;
      }

      const data = snapshot.data() as Record<string, any>;

      return {
        id: snapshot.id,
        allocationType: data.allocationType,
        durationType: data.durationType,
        bucketType: data.bucketType,
        bucket: data.bucket ?? 0,
        startDate: data.startDate,
        endDate: data.endDate ?? null,
        status: (data.status ?? AllocationStatuses.Active) as AllocationStatus,
        clientId: data.clientId ?? null,
        clientName: data.clientName ?? null,
        clientLogo: data.clientLogo ?? null,
        clientResponsible: data.clientResponsible ?? null,
        departmentId: data.departmentId ?? null,
        departmentName: data.departmentName ?? null,
        projectId: data.projectId ?? null,
        projectName: data.projectName ?? null,
        capacityId: data.capacityId ?? null,
        capacityLabel: data.capacityLabel ?? null,
        capacityTypeId: data.capacityTypeId ?? null,
        capacityTypeName: data.capacityTypeName ?? null,
        capacityTechnologyId: data.capacityTechnologyId ?? null,
        capacityTechnologyName: data.capacityTechnologyName ?? null,
        capacitySeniorityId: data.capacitySeniorityId ?? null,
        capacitySeniorityName: data.capacitySeniorityName ?? null,
        rate: parseRate(data.rate),
        responsibleId: data.responsibleId ?? null,
        responsibleName: data.responsibleName ?? null,
        responsibleAvatar: data.responsibleAvatar ?? null,
        createdById: data.createdById ?? '',
        createdByName: data.createdByName ?? '',
        createdByAvatar: data.createdByAvatar ?? null,
        createdAt: data.createdAt,
        updatedAt: data.updatedAt,
        updatedById: data.updatedById ?? null,
        updatedByName: data.updatedByName ?? null,
        updatedByAvatar: data.updatedByAvatar ?? null,
        cancelReason: data.cancelReason ?? null,
        noticeReason: data.noticeReason ?? null,
        terminationAppliedAt: data.terminationAppliedAt ?? null,
        canceledAt: data.canceledAt ?? null,
        canceledById: data.canceledById ?? null,
        canceledByName: data.canceledByName ?? null,
        canceledByAvatar: data.canceledByAvatar ?? null,
      } satisfies CollaboratorAllocation;
    },
  });

  return {
    data: queryResult.data ?? null,
    isLoading: queryResult.isLoading,
    isError: queryResult.isError,
    error: (queryResult.error as Error) ?? null,
    refetch: async () => {
      await queryResult.refetch();
    },
  };
}

export default useCollaboratorAllocationQuery;
